import { Component, DestroyRef, inject, Input } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { Router, ActivatedRoute } from '@angular/router';
import { translate } from '@jsverse/transloco';
import { ConfirmationDialogComponent } from 'src/app/components/confirmation-dialog/confirmation-dialog.component';
import { ErrorDialogComponent } from 'src/app/components/error-dialog/error-dialog.component';
import { Blueprint } from 'src/app/models/blueprints/blueprint';
import { Constants } from 'src/app/models/constants';
import { Instance } from 'src/app/models/instance/instance';
import { RouterSegments } from 'src/app/models/router-segments';
import { BlueprintsService } from 'src/app/services/api/blueprints.service';
import { ComponentsService } from 'src/app/services/api/components.service';
import { InstancesService } from 'src/app/services/api/instances.service';
import { RunbookService } from 'src/app/services/api/runbook.service';
import { AuthService } from 'src/app/services/auth/auth.service';
import { FormUtilService } from 'src/app/services/util/form-util.service';
import { InstallationSpecificationService } from 'src/app/services/util/installation-specification.service';
import { SidenavService } from 'src/app/services/util/sidenav.service';
import { CustomToastrService } from 'src/app/services/util/toastr.service';
import { EditRunbookService } from 'src/app/services/api/edit-runbook.service';
import { DeployStatus } from 'src/app/models/deploy/deploy-status';
import { uniqueByName } from 'src/app/services/util/utils';


@Component({
  selector: 'app-create-runbook',
  templateUrl: './create-runbook.html',
  styleUrl: './create-runbook.scss',
  standalone: false
})
export class CreateRunbook {
  private destroyRef = inject(DestroyRef);
  readonly INSTANCE_DEPLOYING_STATES = [DeployStatus.INITIALIZED, DeployStatus.INITIALIZING, DeployStatus.INITIALIZE_FAILED,
    DeployStatus.DEPLOYING, DeployStatus.TERMINATED, DeployStatus.TERMINATION_FAILED, DeployStatus.TERMINATING,
    DeployStatus.CONFIGURING, DeployStatus.UPDATING, DeployStatus.TESTING, DeployStatus.FAILED,
    DeployStatus.FAILOVER_APPLYING, DeployStatus.FAILBACK_APPLYING,
    DeployStatus.PRE_FAILBACK_APPLYING, DeployStatus.POST_FAILBACK_APPLYING,
    DeployStatus.OPERATION_INPROGRESS, DeployStatus.TENANT_AGENT_CREATION_IN_PROGRESS, DeployStatus.START_FAILED, DeployStatus.STOP_FAILED, DeployStatus.STARTING,
    DeployStatus.STOPPING, DeployStatus.STOPPED, DeployStatus.STARTING_AGENT, DeployStatus.STOPPING_AGENT, DeployStatus.INPROGRESS];
  currentStep = -1;
  runbookForm: FormGroup;
  Constants = Constants;
  instance: Instance;
  blueprint: Blueprint;
  breadcrumbItems: any;
  RouterSegments = RouterSegments;
  components: any;
  selectedComponent: any;
  selectedComponentEdit: any;
  installationSpecificationForms: any;
  versions = [];
  summaryVisited = false;
  filteredComponents: any;
  searchValue: any;
  editData: any;
  isEdit: boolean;
  isNext: boolean;
  componentsLoading = true;
  runbooks: any;

  constructor(private formBuilder: FormBuilder, public authService: AuthService,
    private router: Router, public activatedRoute: ActivatedRoute, private toastrService: CustomToastrService,
    private instanceService: InstancesService, private blueprintsService: BlueprintsService,
    private sidenavService: SidenavService, private componentService: ComponentsService,
    private dialog: MatDialog, private installationSpecificationService: InstallationSpecificationService,
    private runbookService: RunbookService, private editRunbookService: EditRunbookService) {

  }

  ngOnInit(): void {
    this.isEdit = this.router.url.includes('edit');
     this.sidenavService.tenantSelectedMenu = 'TENANT_LAYOUT.MENU.SERVICE_DEFINITION';
    this.runbookForm = this.formBuilder.group({
      name: [undefined, [Validators.required,Validators.minLength(3),
        Validators.pattern(/^([A-Za-z])([a-zA-Z0-9_])+( [a-zA-Z0-9_]+)*$/),Validators.maxLength(36)]],
      description: [undefined, [Validators.required]],
      componentName: [undefined, [Validators.required]],
      componentVersion: [undefined, [Validators.required]]
    });
    this.breadcrumbItems = [
      { icon: 'assets/images/home_icon.svg', url: [RouterSegments.TENANT, this.activatedRoute.snapshot.params.id]},
      { label: translate('TENANT_LAYOUT.MENU.SERVICE_DEFINITION'), url:[RouterSegments.TENANT, this.activatedRoute.snapshot.params.id, RouterSegments.SERVICE_DEFINITION]}
    ];
    if(this.isEdit) this.getSelectedRunbook();
    this.getInstanceDetails();
    this.getComponents();
    this.getRunbooks();
  }

  // to get instance details
  private getInstanceDetails(): void {
    const instanceId = this.activatedRoute.snapshot.params.instanceId;
    this.instanceService.getInstanceDetails(instanceId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(data => {
        if (data) {
          this.instance = data;
          if (this.INSTANCE_DEPLOYING_STATES.includes(this.instance?.status)) {
            this.router.navigate(['/', this.activatedRoute.snapshot.params.id, 'tenant', 'permission-unauthorized'], {skipLocationChange: true});
          }
          this.getBlueprint();
        }
      },
      (error) => {
        setTimeout(() => {
          const title = translate('ERRORS.SERVICE_AVAILABILITY');
          const message = error?.message ? error?.message : (error?.error?.message ? error?.error?.message : (error?.errmsg ? error?.errmsg : translate('SERVICE.INTERNAL_SERVER_ERROR')));
          this.toastrService.errMessage(message, title);
        }, 500);
      });
  }

  // to get blueprint details
  private getBlueprint(): void {
    const blueprintId = this.activatedRoute.snapshot.params.blueprintId;
    const tenantId = this.activatedRoute.snapshot.params.id;
      this.blueprintsService.getBlueprints(tenantId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((blueprints: any) => {
        this.blueprint = blueprints?.find(item => item.id == blueprintId);
        this.breadcrumbItems.push(
          { label: this.blueprint?.name, url: [RouterSegments.TENANT, this.activatedRoute.snapshot.params.id, RouterSegments.SERVICE_DEFINITION,
            this.activatedRoute.snapshot.params.blueprintId] },
          { label: this.instance?.name, url: [RouterSegments.TENANT, this.activatedRoute.snapshot.params.id, RouterSegments.SERVICE_DEFINITION,
            this.activatedRoute.snapshot.params.blueprintId, RouterSegments.ENVIRONMENT, this.activatedRoute.snapshot.params.instanceId] },
          { label: translate('RUNBOOK.RUNBOOK'), action: () => this.goToRunbook()},
          { label:  this.isEdit ?  translate('RUNBOOK.EDIT_RUNBOOK') : translate('INSTANCE_DETAILS.CREATE_RUNBOOK') }
          );
      },
      (error) => {
        setTimeout(() => {
          const title = translate('ERRORS.SERVICE_AVAILABILITY');
          const message = error?.message ? error?.message : (error?.error?.message ? error?.error?.message : (error?.errmsg ? error?.errmsg : translate('SERVICE.INTERNAL_SERVER_ERROR')));
          this.toastrService.errMessage(message, title);
        }, 500);
      });
  }

  getRunbooks(): void {
    this.runbookService.getRunbook(this.activatedRoute.snapshot.params.instanceId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(runbooks => {
        this.runbooks = runbooks;
      },
      (error) => {
        setTimeout(() => {
          const title = translate('ERRORS.SERVICE_AVAILABILITY');
          const message = error?.message ? error?.message : (error?.error?.message ? error?.error?.message : (error?.errmsg ? error?.errmsg : translate('SERVICE.INTERNAL_SERVER_ERROR')));
          this.toastrService.errMessage(message, title);
        }, 500);
      });
  }

  // to load components
  getComponents(): void {
    this.componentsLoading = true;
    const componentType = 'runbook';
      this.componentService.getComponents(componentType)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(data => {
        this.componentsLoading = false;
        this.components = [];
        this.components = data;
        this.filteredComponents = this.components;
        if (this.isEdit && this.editData?.componentName) {
        this.selectedComponentEdit = this.components.filter(c =>
         (c.name === this.editData.componentName || c.displayName === this.editData.componentName) && c.version === this.editData.componentVersion
        );
        this.selectedComponent = this.selectedComponentEdit[0];
        if (this.selectedComponent) {
          this.runbookForm.patchValue({
              name: this.editData?.name,
              description: this.editData?.description,
              componentName: this.selectedComponent,
              componentVersion: this.selectedComponent
          });
        this.filteredComponents = this.components;
        this.versions = this.components.filter(item => (item.name === this.selectedComponent.name && item.version === this.selectedComponent.version));
        this.isNext = true;
      }

      }
      },
      (error) => {
        this.componentsLoading = false;
        const errorMessage = typeof error === 'string' ? error: typeof error?.error === 'string' ? error.error : error;
        this.dialog.open(ErrorDialogComponent, {
          minHeight: '280px',
          width: '630px',
          panelClass: 'flex-dialog',
          data: {
            title:  translate('CUSTOM_IMAGES.GET_COMPONENTS_FAILED'),
            description: errorMessage
          }
        });
      });
  }

  cancelClick(): void {
      this.dialog.open(ConfirmationDialogComponent, {
        panelClass: 'default-dialog-wrapper',
        width: '566px',
        autoFocus: false,
        data: {
          title: this.isEdit ? translate('RUNBOOK.CANCEL_EDIT_RUNBOOK') : translate('RUNBOOK.CANCEL_ADD_RUNBOOK'),
          message: this.isEdit ? translate('RUNBOOK.EDIT_CANCEL_DESCRIPTION') : translate('RUNBOOK.CANCEL_DESCRIPTION'),
          confirmButtonText: translate('BUTTONS.YES'),
          cancelButtonText: translate('BUTTONS.NO')
        }
      }) .afterClosed()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(confirm => {
        if (confirm) {
          this.goToRunbook();
        }
      })

    }

  // to navigate back to environment details page on cancel click
  goToRunbook(): void {
    const tenantId = this.activatedRoute.snapshot.params.id;
    this.router.navigate(['/', this.authService.principal.provider, RouterSegments.TENANT , tenantId, RouterSegments.BLUEPRINTS, this.activatedRoute.snapshot.params.blueprintId,
      RouterSegments.ENVIRONMENT, this.activatedRoute.snapshot.params.instanceId], {state: {data: 'RUNBOOK'}});
  }

  // to validate current step and move backward/forward
  goToStep(step): void {
    const goingBackward = step < this.currentStep;
    if (!goingBackward && this.currentStep === -1 && FormUtilService.validateForm(this.runbookForm)) {
      if (this.isRunbookNameInvalid() && !this.isEdit) {
        this.setErrorToNameControl();
        return;
      }
    }
    if (!goingBackward && ((this.currentStep === -1 && FormUtilService.validateForm(this.runbookForm)) || (this.currentStep === 0 && FormUtilService.validateForm(this.installationSpecificationForms[0].formGroup)))) {
      this.currentStep = step;
      if (this.currentStep === 0) {
        this.installationSpecificationForms = this.installationSpecificationService.buildDynamicForms(
          atob(this.selectedComponent?.inputSpec)
        );
        // to hide the inputs in secret field
        this.installationSpecificationForms.forEach(nestedForm => {
          nestedForm?.property?.properties?.forEach(element => {
            if (element['x-value-type'] === 'secret') {
              element.hide = true;
            }
          });
        });
        if(this.isEdit){
           this.getPreviousVersionData(this.editData?.inputs);
        }

      }
      if (this.currentStep === 1) {
        this.summaryVisited = true;
      }
    } else if (goingBackward) {
      this.currentStep = step;
    }
  }

  private isRunbookNameInvalid(): any {
    const currentRunbookName = this.runbookForm.get('name').value?.toLowerCase();
    return this.runbooks.some(runbook => runbook?.name?.toLowerCase() === currentRunbookName);
  }

  private setErrorToNameControl(): void {
    setTimeout(() => this.runbookForm.get('name').setErrors({nameNotUnique: true}));
  }

    private getPreviousVersionData(installDocRaw): void {
     Object.keys(installDocRaw).forEach(key => {
      const propertyKey = Object.keys(installDocRaw[key]);
      const propertyValue = Object.assign(installDocRaw[key]);
      propertyKey.forEach(controlName => {
        this.installationSpecificationForms.forEach(nestedForm => {
          nestedForm?.formGroup?.controls[key]?.get(controlName)?.setValue(
          propertyValue[controlName] === true ? propertyValue[controlName] : propertyValue[controlName].length === undefined ? '' : propertyValue[controlName]);
          nestedForm?.property?.properties?.forEach(element => {
            if (nestedForm?.formGroup?.controls[key]?.get(element.name)) {
              element.updatedValue = nestedForm.formGroup.controls[key].get(element.name).value;
            }
          });
        });
      });
    });
    setTimeout(() => {
      this.installationSpecificationForms.forEach(f => {
        f.formGroup.markAsPristine();
      });
    }, 0);

  }

  isFormDirty() {
  return this.installationSpecificationForms.some(f => f.formGroup.dirty);
}



  // assign the selected component
  componentSelection(event): void {
    this.selectedComponent = event.value;
    this.versions = this.components.filter(item => item.name === this.selectedComponent.name);
    const versionsSorted = this.versions.sort((a,b) => a.dateCreated < b.dateCreated ? 1 : a.dateCreated > b.dateCreated ? -1 : 0);
    this.runbookForm.get('componentVersion').setValue(versionsSorted[0]);
  }

  // searching components dropdown
  searchChanged(value): void {
    const filtered = this.components.filter(item => item?.name?.toLowerCase().includes(value.toLowerCase()));
    this.filteredComponents = uniqueByName(filtered);
  }

  // resetting dropdown values when dropdown is closed
  dropdownClose(): void {
    if (this.filteredComponents?.length === 0) {
      this.filteredComponents = uniqueByName(this.components);
    }
  }

  // resetting search filter of components dropdown when dropdown is closed
  resetFilter(): void {
    this.searchValue = undefined;
    this.filteredComponents = uniqueByName(this.components);
  }

  submit(): void {
    this.dialog.open(ConfirmationDialogComponent, {
      panelClass: 'default-dialog-wrapper',
      width: '566px',
      autoFocus: false,
      data: {
        title: this.isEdit ? translate('RUNBOOK.EDIT_RUNBOOK') : translate('INSTANCE_DETAILS.CREATE_RUNBOOK'),
        message: this.isEdit ? translate('INSTANCE_DETAILS.EDIT_RUNBOOK_MESSAGE') : translate('INSTANCE_DETAILS.CREATE_RUNBOOK_MESSAGE'),
        confirmButtonText: translate('BUTTONS.CONFIRM')
      }
    })
      .afterClosed()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(confirm => {
        if (confirm) {
        const installationDocument = {};
        this.installationSpecificationForms.forEach(installationSpecificationForm => {
          const installationSpecificationFormValue = this.installationSpecificationService.getFormValue(
            installationSpecificationForm);
          Object.assign(installationDocument, installationSpecificationFormValue);
        });
       if(this.isEdit){
            const obj = {
            id: this.activatedRoute.snapshot.params.instanceId,
            runbookId: this.editData.id,
            inputs: installationDocument
          }
          this.runbookService.patchRunbook(obj).pipe(takeUntilDestroyed(this.destroyRef))
          .subscribe(data => {
            const tenantId = this.activatedRoute.snapshot.params.id;
            this.router.navigate(['/', this.authService.principal.provider, RouterSegments.TENANT , tenantId, RouterSegments.BLUEPRINTS, this.activatedRoute.snapshot.params.blueprintId,
              RouterSegments.ENVIRONMENT, this.activatedRoute.snapshot.params.instanceId], {state: {data: 'RUNBOOK'}});
            setTimeout(() => {
              const title = translate('RUNBOOK.EDIT_RUNBOOK');
              const message = translate('INSTANCE_DETAILS.RUNBOOK_UPDATION_TRIGGERED');
              this.toastrService.successMessage(message, title);
            }, 500);
          },
          (error) => {
            const errorMessage = typeof error === 'string' ? error: typeof error?.error === 'string' ? error.error : error;
            this.dialog.open(ErrorDialogComponent, {
              minHeight: '280px',
              width: '630px',
              panelClass: 'flex-dialog',
              data: {
                title:  translate('RUNBOOK.EDIT_RUNBOOK_FAILED'),
                description: errorMessage
              }
            });
          });
        }

        else{
          const obj = {
          id: this.activatedRoute.snapshot.params.instanceId,
          name: this.runbookForm.get('name')?.value,
          description: this.runbookForm.get('description')?.value,
          componentName: this.runbookForm.get('componentName')?.value?.name,
          componentVersion: this.runbookForm.get('componentVersion')?.value?.version,
          inputs: installationDocument
        }
          this.runbookService.postRunbook(obj).pipe(takeUntilDestroyed(this.destroyRef))
          .subscribe(data => {
            const tenantId = this.activatedRoute.snapshot.params.id;
            this.router.navigate(['/', this.authService.principal.provider, RouterSegments.TENANT , tenantId, RouterSegments.BLUEPRINTS, this.activatedRoute.snapshot.params.blueprintId,
              RouterSegments.ENVIRONMENT, this.activatedRoute.snapshot.params.instanceId], {state: {data: 'RUNBOOK'}});
            setTimeout(() => {
              const title = translate('INSTANCE_DETAILS.CREATE_RUNBOOK');
              const message = translate('INSTANCE_DETAILS.RUNBOOK_CREATION_TRIGGERED');
              this.toastrService.successMessage(message, title);
            }, 500);
          },
          (error) => {
            const errorMessage = typeof error === 'string' ? error: typeof error?.error === 'string' ? error.error : error;
            this.dialog.open(ErrorDialogComponent, {
              minHeight: '280px',
              width: '630px',
              panelClass: 'flex-dialog',
              data: {
                title:  translate('INSTANCE_DETAILS.CREATE_RUNBOOK_FAILED'),
                description: errorMessage
              }
            });
          });
        }
        }

        this.instanceService.triggerRefresh();

      });
  }

   getSelectedRunbook(){
     const runbookId = this.activatedRoute.snapshot.params.runbookId
    this.runbookService.getRunbookById(this.activatedRoute.snapshot.params.instanceId, runbookId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(response => {
        this.editData = response;
        this.getComponents();
      },
      (error) => {
        const errorMessage = typeof error === 'string' ? error: typeof error?.error === 'string' ? error.error : error;
        this.dialog.open(ErrorDialogComponent, {
          minHeight: '280px',
          width: '630px',
          panelClass: 'flex-dialog',
          data: {
            title:  translate('RUNBOOK.GET_RUNBOOK_DETAILS_FAILED'),
            description: errorMessage
          }
        });
      });
  }


}
